import { useState, useCallback } from 'react';
import { Uuid } from '@/api/types';
import { ResourceWarning, WarningType } from './types';

export const useResourceWarnings = () => {
  const [warnings, setWarnings] = useState<ResourceWarning[]>([]);

  const addWarning = useCallback((warning: ResourceWarning) => {
    setWarnings((prev) => {
      const alreadyExists = prev.some((w) => w.id === warning.id);
      return alreadyExists ? prev : [...prev, warning];
    });
  }, []);

  const removeWarning = useCallback((id: string) => {
    setWarnings((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const removeWarningsByEmployee = useCallback((employeeId: Uuid, type?: WarningType) => {
    setWarnings((prev) =>
      prev.filter((w) => {
        if (type) {
          return !(w.employeeId === employeeId && w.type === type);
        }
        return w.employeeId !== employeeId;
      }),
    );
  }, []);

  const toggleWarning = useCallback((warning: ResourceWarning) => {
    setWarnings((prev) => {
      const exists = prev.some((w) => w.id === warning.id);
      if (exists) {
        return prev.filter((w) => w.id !== warning.id);
      }
      return [...prev, warning];
    });
  }, []);

  const clearWarnings = useCallback(() => {
    setWarnings([]);
  }, []);

  const getWarningsByType = useCallback((type: WarningType) => warnings.filter((w) => w.type === type), [warnings]);

  const getWarningsByEmployee = useCallback(
    (employeeId: Uuid) => warnings.filter((w) => w.employeeId === employeeId),
    [warnings],
  );

  return {
    warnings,
    addWarning,
    removeWarning,
    removeWarningsByEmployee,
    toggleWarning,
    clearWarnings,
    getWarningsByType,
    getWarningsByEmployee,
  };
};
